/**
 * Loot Filter System - Controls visibility and display of dropped items on the ground.
 * Cycles through filter levels: show_all -> normal -> strict
 * Each level adjusts opacity, label size, and visual emphasis of ground items by rarity.
 *
 * Features:
 * - Rarity-based opacity filtering (show_all / normal / strict)
 * - Item level threshold filtering (hide items below playerLevel - 3)
 * - Mini notification popups when picking up items (name + rarity color, fades 2s)
 * - Visible filter indicator with icon in HUD corner
 */

import { ItemRarity } from '@shared/types';
import { ITEM } from '@shared/constants';
import { t } from '@locales/index';

export type FilterLevel = 'show_all' | 'normal' | 'strict';

// Localized display names for filter levels
function getFilterLevelName(level: FilterLevel): string {
  const keys: Record<FilterLevel, string> = {
    show_all: 'lootFilter.showAll',
    normal: 'lootFilter.normal',
    strict: 'lootFilter.strict',
  };
  return t(keys[level]);
}

/** Pickup notification entry displayed briefly on screen */
interface PickupNotification {
  itemName: string;
  rarity: ItemRarity;
  life: number;     // Remaining time in seconds (starts at 2.0)
  maxLife: number;   // Total life for alpha calculation
}

export class LootFilter {
  private level: FilterLevel = 'show_all';
  private static instance: LootFilter | null = null;

  // Notification state for showing the filter level change
  private notificationLife: number = 0;

  // Item level threshold: items below (playerLevel - this) are hidden in normal/strict
  private levelThreshold: number = 3;

  // Current player level (updated each frame by GameWorld)
  private playerLevel: number = 1;

  // Pickup notification queue (newest at end)
  private pickupNotifications: PickupNotification[] = [];
  private static readonly MAX_NOTIFICATIONS = 5;
  private static readonly NOTIFICATION_DURATION = 2.0;

  static getInstance(): LootFilter {
    if (!this.instance) this.instance = new LootFilter();
    return this.instance;
  }

  cycleFilter(): void {
    switch (this.level) {
      case 'show_all': this.level = 'normal'; break;
      case 'normal': this.level = 'strict'; break;
      case 'strict': this.level = 'show_all'; break;
    }
    this.notificationLife = 2.0; // Show notification for 2 seconds
  }

  getLevel(): FilterLevel {
    return this.level;
  }

  getDisplayName(): string {
    return getFilterLevelName(this.level);
  }

  getNotificationLife(): number {
    return this.notificationLife;
  }

  /** Set the current player level for item-level threshold filtering */
  setPlayerLevel(level: number): void {
    this.playerLevel = level;
  }

  getPlayerLevel(): number {
    return this.playerLevel;
  }

  /** Check whether an item should be hidden by the level threshold filter.
   *  Returns true if the item level is too low (below playerLevel - threshold).
   *  Only applies in 'normal' and 'strict' filter modes.
   */
  isItemBelowLevelThreshold(itemLevel: number): boolean {
    if (this.level === 'show_all') return false;
    if (itemLevel <= 0) return false; // Currency or quest items with level 0 are never hidden
    return itemLevel < (this.playerLevel - this.levelThreshold);
  }

  /** Add a pickup notification for an item that was just collected */
  addPickupNotification(itemName: string, rarity: ItemRarity): void {
    this.pickupNotifications.push({
      itemName,
      rarity,
      life: LootFilter.NOTIFICATION_DURATION,
      maxLife: LootFilter.NOTIFICATION_DURATION,
    });

    // Trim old notifications if we exceed the max
    while (this.pickupNotifications.length > LootFilter.MAX_NOTIFICATIONS) {
      this.pickupNotifications.shift();
    }
  }

  getPickupNotifications(): ReadonlyArray<PickupNotification> {
    return this.pickupNotifications;
  }

  update(deltaTime: number): void {
    if (this.notificationLife > 0) {
      this.notificationLife -= deltaTime;
      if (this.notificationLife < 0) this.notificationLife = 0;
    }

    // Update pickup notifications
    for (let i = this.pickupNotifications.length - 1; i >= 0; i--) {
      this.pickupNotifications[i].life -= deltaTime;
      if (this.pickupNotifications[i].life <= 0) {
        this.pickupNotifications[i] = this.pickupNotifications[this.pickupNotifications.length - 1];
        this.pickupNotifications.pop();
      }
    }
  }

  /**
   * Returns opacity for an item based on the current filter level.
   * 0 = completely hidden, 1 = fully visible, 0.15 = dimmed/faded.
   */
  getItemOpacity(rarity: ItemRarity): number {
    switch (this.level) {
      case 'show_all':
        return 1.0;
      case 'normal':
        // Normal items are dimmed, everything else full
        if (rarity === 'normal') return 0.15;
        return 1.0;
      case 'strict':
        // Only rare+ fully visible; normal hidden, magic dimmed
        if (rarity === 'normal') return 0;
        if (rarity === 'magic') return 0.15;
        return 1.0;
    }
  }

  /**
   * Combined check: filter by rarity + level threshold.
   * Returns effective opacity (0 = hidden).
   */
  getEffectiveItemOpacity(rarity: ItemRarity, itemLevel: number): number {
    // Level threshold hides items entirely in normal/strict
    if (this.isItemBelowLevelThreshold(itemLevel)) {
      return 0;
    }
    return this.getItemOpacity(rarity);
  }

  /**
   * Get label font size based on rarity (higher rarity = bigger label).
   */
  getLabelFontSize(rarity: ItemRarity): number {
    switch (rarity) {
      case 'legendary': return 13;
      case 'set': return 12;
      case 'rare': return 11;
      case 'magic': return 10;
      default: return 9;
    }
  }

  /**
   * Get rarity color from the shared constants.
   */
  getRarityColor(rarity: ItemRarity): string {
    return ITEM.COLORS[rarity] || '#a0a0a0';
  }

  /**
   * Get a slightly brighter border color for ground labels.
   */
  getRarityBorderColor(rarity: ItemRarity): string {
    switch (rarity) {
      case 'legendary': return '#ffc870';
      case 'set': return '#2ecc71';
      case 'rare': return '#e8dc80';
      case 'magic': return '#7ca0e0';
      default: return '#666666';
    }
  }

  /**
   * Check whether an item should be interactable (pickable) under current filter.
   * Items with 0 opacity are not pickable.
   */
  isItemInteractable(rarity: ItemRarity): boolean {
    return this.getItemOpacity(rarity) > 0;
  }
}

// ============================================================
// Filter level indicator icon (funnel shape drawn via canvas)
// ============================================================

function drawFilterIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  // Funnel top
  ctx.moveTo(x - size * 0.5, y - size * 0.4);
  ctx.lineTo(x + size * 0.5, y - size * 0.4);
  // Funnel narrowing
  ctx.lineTo(x + size * 0.1, y + size * 0.1);
  // Stem
  ctx.lineTo(x + size * 0.1, y + size * 0.45);
  ctx.lineTo(x - size * 0.1, y + size * 0.45);
  ctx.lineTo(x - size * 0.1, y + size * 0.1);
  ctx.closePath();
  ctx.fill();
}

/**
 * Render the loot filter level indicator on the HUD.
 * Shows a funnel icon + text near the top-right area indicating current filter mode.
 */
export function renderLootFilterIndicator(
  ctx: CanvasRenderingContext2D,
  filter: LootFilter,
  screenWidth: number,
  _screenHeight: number,
): void {
  const level = filter.getLevel();

  // Always show a subtle indicator; brighter when recently changed
  const notificationLife = filter.getNotificationLife();
  const baseAlpha = 0.5;
  const notifyAlpha = notificationLife > 0
    ? Math.min(1.0, baseAlpha + (1.0 - baseAlpha) * Math.min(1, notificationLife / 0.5))
    : baseAlpha;

  ctx.save();
  ctx.globalAlpha = notifyAlpha;

  const label = `${t('lootFilter.label')}: ${filter.getDisplayName()}`;
  const fontSize = 12;
  ctx.font = `bold ${fontSize}px Arial`;

  const iconSize = 14;
  const iconPadding = 4;
  const textWidth = ctx.measureText(label).width;
  const padding = 8;
  const totalContentW = iconSize + iconPadding + textWidth;
  const bgW = totalContentW + padding * 2;
  const bgH = fontSize + padding * 2 + 2;
  const x = screenWidth - bgW - 10;
  const y = 50;

  // Background with slightly more visibility
  ctx.fillStyle = 'rgba(10, 10, 20, 0.8)';
  ctx.beginPath();
  ctx.roundRect(x, y, bgW, bgH, 5);
  ctx.fill();

  // Border color based on filter level
  let borderColor: string;
  switch (level) {
    case 'show_all': borderColor = '#888888'; break;
    case 'normal': borderColor = '#5c8ed4'; break;
    case 'strict': borderColor = '#d4875c'; break;
  }
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Glow pulse when recently changed
  if (notificationLife > 0) {
    const glowAlpha = Math.min(0.4, notificationLife * 0.3);
    ctx.shadowColor = borderColor;
    ctx.shadowBlur = 8 * glowAlpha / 0.4;
  }

  // Filter icon (funnel)
  const iconX = x + padding + iconSize / 2;
  const iconY = y + bgH / 2;
  drawFilterIcon(ctx, iconX, iconY, iconSize, borderColor);
  ctx.shadowBlur = 0;

  // Text
  ctx.fillStyle = borderColor;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x + padding + iconSize + iconPadding, y + bgH / 2);

  ctx.restore();

  // Render pickup notifications (below the filter indicator)
  renderPickupNotifications(ctx, filter, screenWidth, y + bgH + 8);
}

/**
 * Render the mini pickup notification popups.
 * Shows a brief text with item name colored by rarity, fading out over 2s.
 */
function renderPickupNotifications(
  ctx: CanvasRenderingContext2D,
  filter: LootFilter,
  screenWidth: number,
  startY: number,
): void {
  const notifications = filter.getPickupNotifications();
  if (notifications.length === 0) return;

  ctx.save();
  const lineHeight = 22;
  const padding = 6;

  for (let i = 0; i < notifications.length; i++) {
    const notif = notifications[i];
    const alpha = Math.min(1, notif.life / 0.5); // Fade out in last 0.5s
    const slideIn = Math.min(1, (notif.maxLife - notif.life) / 0.15); // Slide in over 0.15s
    const offsetX = (1 - slideIn) * 50; // Slide from right

    ctx.globalAlpha = alpha * 0.95;

    const rarityColor = filter.getRarityColor(notif.rarity);
    const text = `+ ${notif.itemName}`;
    ctx.font = `bold 11px Arial`;
    const textW = ctx.measureText(text).width;
    const bgW = textW + padding * 2 + 8;
    const bgH = lineHeight - 4;
    const nx = screenWidth - bgW - 10 + offsetX;
    const ny = startY + i * lineHeight;

    // Background
    ctx.fillStyle = 'rgba(10, 10, 20, 0.75)';
    ctx.beginPath();
    ctx.roundRect(nx, ny, bgW, bgH, 3);
    ctx.fill();

    // Left color accent bar
    ctx.fillStyle = rarityColor;
    ctx.fillRect(nx, ny, 3, bgH);

    // Text
    ctx.fillStyle = rarityColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, nx + padding + 4, ny + bgH / 2);
  }

  ctx.restore();
}
